import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { kits, categories } from '../data/products';

const KitCard = ({ kit, onBuy }) => {
  return (
    <div className="kit-card">
      <div className="kit-card-header">
        <span className="kit-icon">{kit.icon}</span>
        <span className="kit-category-badge">{categories[kit.category]?.name}</span>
      </div>
      <h3 className="kit-name">{kit.name}</h3>
      <p className="kit-description">{kit.description}</p>
      <div className="kit-stats">
        <span className="kit-questions">{kit.questionCount} Questions</span>
      </div>
      <div className="kit-topics">
        {kit.topics.slice(0, 4).map((topic, i) => (
          <span key={i} className="kit-topic-tag">{topic}</span>
        ))}
        {kit.topics.length > 4 && (
          <span className="kit-topic-more">+{kit.topics.length - 4} more</span>
        )}
      </div>
      <div className="kit-footer">
        <div className="kit-price">
          <span className="original-price">₹{kit.price * 10}</span>
          <span className="current-price">
            <span className="currency">₹</span>
            <span className="amount">{kit.price}</span>
          </span>
        </div>
        <div className="kit-actions">
          <Link to={`/kits/${kit.slug}`} className="btn btn-outline btn-small">
            View Details
          </Link>
          <button
            onClick={() => onBuy({ id: kit.id, name: kit.name, price: kit.price })}
            className="btn btn-primary btn-small"
          >
            Get Kit
          </button>
        </div>
      </div>
    </div>
  );
};

const AllKitsPage = () => {
  const { openCheckout } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredKits = useMemo(() => {
    return kits.filter(kit => {
      const matchesCategory = activeCategory === 'all' || kit.category === activeCategory;
      const matchesSearch = searchQuery === '' ||
        kit.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        kit.topics.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const categoryList = [
    { id: 'all', name: 'All Kits', count: kits.length },
    ...Object.values(categories).map(cat => ({
      ...cat,
      count: kits.filter(k => k.category === cat.id).length
    }))
  ];

  return (
    <div className="kits-page">
      {/* Hero Section */}
      <section className="kits-hero">
        <div className="container">
          <h1 className="kits-title">Interview Kits</h1>
          <p className="kits-subtitle">
            16 comprehensive interview preparation kits covering all major technologies.
            Each kit contains curated questions from real interviews at top companies.
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section className="kits-filters">
        <div className="container">
          <div className="filters-wrapper">
            {/* Search */}
            <div className="search-wrapper">
              <input
                type="text"
                placeholder="Search kits or topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="kits-search"
              />
            </div>

            {/* Category Tabs */}
            <div className="category-tabs">
              {categoryList.map(cat => (
                <button
                  key={cat.id}
                  className={`category-tab ${activeCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.icon && <span className="tab-icon">{cat.icon}</span>}
                  <span className="tab-name">{cat.name}</span>
                  <span className="tab-count">{cat.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Kits Grid */}
      <section className="kits-grid-section">
        <div className="container">
          {filteredKits.length > 0 ? (
            <>
              <p className="results-count">
                Showing {filteredKits.length} {filteredKits.length === 1 ? 'kit' : 'kits'}
                {activeCategory !== 'all' && ` in ${categories[activeCategory]?.name}`}
              </p>
              <div className="kits-grid">
                {filteredKits.map(kit => (
                  <KitCard key={kit.id} kit={kit} onBuy={openCheckout} />
                ))}
              </div>
            </>
          ) : (
            <div className="no-results">
              <span className="no-results-icon">🔍</span>
              <h3>No kits found</h3>
              <p>Try adjusting your search or filter criteria</p>
              <button
                className="btn btn-outline"
                onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Bundle CTA */}
      <section className="bundle-cta-section">
        <div className="container">
          <div className="bundle-cta-card">
            <div className="bundle-cta-content">
              <span className="bundle-cta-badge">Best Value</span>
              <h2>Want all 16 kits?</h2>
              <p>Get the Complete Collection Bundle and save ₹750+</p>
              <div className="bundle-cta-price">
                <span className="original">₹1,749</span>
                <span className="current">₹999</span>
              </div>
            </div>
            <div className="bundle-cta-actions">
              <Link to="/bundles/complete-collection" className="btn btn-primary btn-large">
                View Complete Collection
              </Link>
              <Link to="/bundles" className="btn btn-outline">
                Browse All Bundles
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AllKitsPage;
